// === OVERLAY FEEDBACK / SUGGESTION ===
// DOM overlay simplifié pour soumettre un retour d'amélioration.
// Volontairement plus léger que le bug report : pas d'attachements
// (pas de screenshot/save/logs), juste pseudo + catégorie + message.

import { PlayerProfile } from '../systems/PlayerProfile';
import { GAME_VERSION } from '../systems/MetaManager';
import {
  submitFeedback, generateFeedbackId, FEEDBACK_CATEGORIES, FeedbackCategory,
} from '../systems/FeedbackSender';

export function showFeedbackOverlay(currentScene: string, onClose?: () => void): void {
  const initialPseudo = PlayerProfile.getPseudo() ?? '';
  // ID pré-généré, affiché à l'utilisateur et utilisé tel quel par le sender.
  const reportId = generateFeedbackId();
  const shortId = reportId.split('_').pop() ?? reportId;

  const overlay = document.createElement('div');
  overlay.style.cssText = [
    'position:fixed', 'top:0', 'left:0', 'width:100%', 'height:100%',
    'background:rgba(0,0,0,0.88)', 'z-index:9999',
    'display:flex', 'align-items:center', 'justify-content:center',
  ].join(';');

  // Bloque la propagation des events au canvas Phaser (idem BugReportOverlay)
  const stopAll = (e: Event) => e.stopPropagation();
  ['keydown', 'keyup', 'keypress',
    'mousedown', 'mouseup', 'click',
    'pointerdown', 'pointerup', 'pointermove',
    'touchstart', 'touchend', 'touchmove',
    'wheel'].forEach(t => overlay.addEventListener(t, stopAll));

  const box = document.createElement('div');
  box.style.cssText = [
    'background:#1a1a2e', 'border:2px solid #f1c40f', 'border-radius:8px',
    'padding:18px 18px', 'width:min(420px, 92vw)', 'box-sizing:border-box',
    'max-height:90vh', 'overflow-y:auto',
    'font-family:monospace', 'color:#ecf0f1',
  ].join(';');

  // Titre
  const title = document.createElement('div');
  title.textContent = '💡 Suggestion / amélioration';
  title.style.cssText = 'font-size:16px;color:#f1c40f;font-weight:bold;margin-bottom:4px;';

  const subtitle = document.createElement('div');
  subtitle.textContent = 'Une idée pour améliorer le jeu ? Partage-la.';
  subtitle.style.cssText = 'font-size:11px;color:#7f8c8d;margin-bottom:14px;';

  // Pseudo
  const pseudoLabel = document.createElement('div');
  pseudoLabel.textContent = 'Ton pseudo de testeur :';
  pseudoLabel.style.cssText = 'font-size:11px;color:#f39c12;margin-bottom:4px;';
  const pseudoInput = document.createElement('input');
  pseudoInput.type = 'text';
  pseudoInput.maxLength = 24;
  pseudoInput.placeholder = 'ex: Capitaine Rouillé';
  pseudoInput.value = initialPseudo;
  pseudoInput.style.cssText = [
    'width:100%', 'box-sizing:border-box',
    'background:#0d1117', 'border:1px solid #3a536b', 'border-radius:4px',
    'color:#f39c12', 'font-family:monospace', 'font-size:12px',
    'padding:6px 10px', 'margin-bottom:12px', 'outline:none',
  ].join(';');

  // Catégorie : 4 boutons radio stylisés
  const catLabel = document.createElement('div');
  catLabel.textContent = 'Catégorie :';
  catLabel.style.cssText = 'font-size:11px;color:#f39c12;margin-bottom:4px;';
  const catRow = document.createElement('div');
  catRow.style.cssText = 'display:flex;flex-wrap:wrap;gap:4px;margin-bottom:12px;';

  let selectedCategory: FeedbackCategory = 'suggestion';
  const catButtons: HTMLButtonElement[] = [];

  const refreshCatButtons = () => {
    catButtons.forEach((b, i) => {
      const isActive = FEEDBACK_CATEGORIES[i].id === selectedCategory;
      b.style.background = isActive ? '#2c2a1a' : '#0d1117';
      b.style.color = isActive ? '#f1c40f' : '#7f8c8d';
      b.style.borderColor = isActive ? '#f1c40f' : '#3a536b';
    });
  };

  for (const cat of FEEDBACK_CATEGORIES) {
    const b = document.createElement('button');
    b.textContent = `${cat.emoji} ${cat.label}`;
    b.style.cssText = [
      'flex:1 1 auto', 'min-width:48%',
      'background:#0d1117', 'border:1px solid #3a536b', 'border-radius:4px',
      'color:#7f8c8d', 'font-family:monospace', 'font-size:11px',
      'padding:6px 4px', 'cursor:pointer',
    ].join(';');
    b.addEventListener('click', () => {
      selectedCategory = cat.id;
      refreshCatButtons();
    });
    catRow.appendChild(b);
    catButtons.push(b);
  }
  refreshCatButtons();

  // Message
  const textarea = document.createElement('textarea');
  textarea.rows = 5;
  textarea.maxLength = 1500;
  textarea.placeholder = 'Décris ton idée ou ton ressenti (ex: "Le combat boss serait plus tendu si les sbires ne mouraient pas tous d\'un coup")';
  textarea.style.cssText = [
    'width:100%', 'box-sizing:border-box', 'resize:vertical',
    'background:#0d1117', 'border:1px solid #f1c40f', 'border-radius:4px',
    'color:#ecf0f1', 'font-family:monospace', 'font-size:12px',
    'padding:8px 10px', 'margin-bottom:10px', 'outline:none',
  ].join(';');

  // Infos auto (légères : pas de UA, pas de résolution — pas pertinent pour une suggestion)
  const autoInfo = document.createElement('div');
  autoInfo.style.cssText = 'font-size:10px;color:#444;margin-bottom:14px;line-height:1.7;';
  autoInfo.innerHTML = [
    `<span style="color:#555">Infos auto :</span>`,
    `<span style="color:#f1c40f">ID&nbsp;: <code>#${shortId}</code></span> <span style="color:#555">(${reportId})</span>`,
    `Version&nbsp;: v${GAME_VERSION}`,
    `Scène&nbsp;: ${currentScene}`,
  ].join('<br>');

  const feedback = document.createElement('div');
  feedback.style.cssText = 'font-size:11px;margin-bottom:10px;min-height:16px;';

  const btnRow = document.createElement('div');
  btnRow.style.cssText = 'display:flex;gap:12px;';

  const cancelBtn = document.createElement('button');
  cancelBtn.textContent = '[ Annuler ]';
  cancelBtn.style.cssText = [
    'flex:1', 'background:#1e1e1e', 'border:1px solid #555', 'border-radius:4px',
    'color:#7f8c8d', 'font-family:monospace', 'font-size:12px',
    'padding:8px', 'cursor:pointer',
  ].join(';');
  cancelBtn.addEventListener('mouseover', () => { cancelBtn.style.color = '#bdc3c7'; });
  cancelBtn.addEventListener('mouseout', () => { cancelBtn.style.color = '#7f8c8d'; });

  const submitBtn = document.createElement('button');
  submitBtn.textContent = '[ 📤 Envoyer ]';
  submitBtn.style.cssText = [
    'flex:2', 'background:#2c2a1a', 'border:1px solid #f1c40f', 'border-radius:4px',
    'color:#f1c40f', 'font-family:monospace', 'font-size:12px',
    'padding:8px', 'cursor:pointer',
  ].join(';');
  submitBtn.addEventListener('mouseover', () => { submitBtn.style.color = '#fff5b3'; });
  submitBtn.addEventListener('mouseout', () => { submitBtn.style.color = '#f1c40f'; });

  const close = () => { overlay.remove(); onClose?.(); };
  cancelBtn.addEventListener('click', close);

  submitBtn.addEventListener('click', async () => {
    const msg = textarea.value.trim();
    if (msg.length < 5) {
      feedback.textContent = 'Décris ton idée (5 caractères minimum).';
      feedback.style.color = '#e74c3c';
      return;
    }
    const pseudoRaw = pseudoInput.value.trim();
    const pseudo = pseudoRaw.length > 0 ? pseudoRaw : 'Anonyme';
    if (pseudoRaw.length > 0 && pseudoRaw !== initialPseudo) PlayerProfile.setPseudo(pseudoRaw);

    submitBtn.disabled = true;
    submitBtn.textContent = '[ … envoi en cours ]';
    feedback.textContent = '';

    const result = await submitFeedback({
      pseudo,
      category: selectedCategory,
      message: msg,
      version: GAME_VERSION,
      scene: currentScene,
    }, reportId);

    feedback.textContent = result.message;
    feedback.style.color = result.sentToDiscord ? '#2ecc71' : (result.ok ? '#f39c12' : '#e74c3c');
    if (result.ok) {
      setTimeout(close, 2200);
    } else {
      submitBtn.disabled = false;
      submitBtn.textContent = '[ 📤 Envoyer ]';
    }
  });

  btnRow.appendChild(cancelBtn);
  btnRow.appendChild(submitBtn);

  box.appendChild(title);
  box.appendChild(subtitle);
  box.appendChild(pseudoLabel);
  box.appendChild(pseudoInput);
  box.appendChild(catLabel);
  box.appendChild(catRow);
  box.appendChild(textarea);
  box.appendChild(autoInfo);
  box.appendChild(feedback);
  box.appendChild(btnRow);
  overlay.appendChild(box);
  document.body.appendChild(overlay);

  setTimeout(() => {
    if (initialPseudo.length === 0) pseudoInput.focus();
    else textarea.focus();
  }, 50);
}
